import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCaseStudy } from '../api';
import { marked } from 'marked';

export default function CaseStudyDetailPage() {
  const { slug } = useParams();
  const [study, setStudy] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getCaseStudy(slug).then(setStudy).catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <div className="pt-28 pb-20 text-center">
      <h1 className="text-2xl font-bold mb-4">Case Study Not Found</h1>
      <Link to="/case-studies" className="text-blue-600 hover:underline">View all case studies</Link>
    </div>
  );

  if (!study) return (
    <div className="pt-28 pb-20 text-center">
      <div className="animate-pulse text-gray-400">Loading...</div>
    </div>
  );

  const metrics = study.metrics || [];
  const technologies = study.technologies || [];

  return (
    <div className="pt-24">
      {/* Hero */}
      <div className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <Link to="/case-studies" className="text-blue-400 hover:text-blue-300 text-sm mb-4 inline-block">&larr; All Case Studies</Link>
          <div className="flex items-center gap-4 mb-4">
            {study.client_logo && <img src={study.client_logo} alt={study.client_name} className="h-12 w-12 rounded-lg object-cover bg-white p-1" />}
            <span className="text-blue-400 font-medium">{study.client_name}</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">{study.title}</h1>
          <p className="text-xl text-gray-300 max-w-3xl">{study.excerpt}</p>
        </div>
      </div>

      {/* Metrics Bar */}
      {metrics.length > 0 && (
        <div className="bg-blue-600 py-10">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className={`grid grid-cols-2 md:grid-cols-${Math.min(metrics.length, 4)} gap-8 text-center text-white`}>
              {metrics.map((m, i) => (
                <div key={i}>
                  <div className="text-3xl md:text-4xl font-bold mb-1">{m.value}</div>
                  <div className="text-blue-100">{m.label}</div>
                  {m.detail && <div className="text-sm text-blue-200 mt-1">{m.detail}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hero Image */}
      {study.hero_image && (
        <div className="container mx-auto px-4 md:px-6 lg:px-8 -mt-8 relative z-10">
          <img src={study.hero_image} alt={study.title} className="w-full max-h-96 object-cover rounded-xl shadow-xl" />
        </div>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          {study.challenge && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-4">The Challenge</h2>
              <div className="prose prose-lg max-w-none text-gray-600" dangerouslySetInnerHTML={{ __html: marked(study.challenge) }} />
            </div>
          )}
          {study.solution && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-4">Our Solution</h2>
              <div className="prose prose-lg max-w-none text-gray-600" dangerouslySetInnerHTML={{ __html: marked(study.solution) }} />
            </div>
          )}
          {study.results && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-4">The Results</h2>
              <div className="prose prose-lg max-w-none text-gray-600" dangerouslySetInnerHTML={{ __html: marked(study.results) }} />
            </div>
          )}

          {/* Technologies */}
          {technologies.length > 0 && (
            <div className="mb-12">
              <h3 className="text-lg font-semibold mb-3">Technologies Used</h3>
              <div className="flex flex-wrap gap-2">
                {technologies.map(t => (
                  <span key={t} className="bg-blue-50 text-blue-700 text-sm font-medium px-3 py-1 rounded-full">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Testimonial */}
          {study.testimonial_quote && (
            <div className="bg-gray-50 p-8 rounded-xl border-l-4 border-blue-600 my-12">
              <p className="text-lg text-gray-700 italic mb-4">&ldquo;{study.testimonial_quote}&rdquo;</p>
              {study.testimonial_author && (
                <div className="font-semibold text-gray-900">
                  {study.testimonial_author}
                  {study.testimonial_role && <span className="text-gray-500 font-normal"> — {study.testimonial_role}</span>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-blue-600 py-16 text-center text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Want Results Like This?</h2>
          <p className="text-xl text-blue-100 mb-8">Book a free growth audit and see how we can drive revenue for your business.</p>
          <Link to="/growth-audit" className="bg-white text-blue-600 hover:bg-blue-50 py-3 px-8 rounded-full font-medium text-lg transition inline-block">
            Get Your Free Growth Audit
          </Link>
        </div>
      </div>
    </div>
  );
}
